// GPH Expert Intelligence Core — Context Engine (PDS §7.4).
// Rastreia o que já aconteceu na conversa para impedir respostas desconectadas:
//   - diagnóstico de spray nunca gera treino de cover;
//   - "treinar essa situação" usa o último resultado;
//   - validação com vídeo se associa ao diagnóstico/treino anterior;
//   - pedido de PUBG nunca recebe lógica de Free Fire.
//
// Persistência: campo `gphContext` no doc users/{uid}/coach_state/gph_agent
// (via saveAgentState existente, que já faz strip de chaves sensíveis).
// playerMemory é PERMANENTE — startNewConversation limpa a conversa, não o perfil.

export const createContext = () => ({
  // Início desta conversa: resultados com `at` anterior a este carimbo são
  // contexto de sessões antigas (reidratado) e nunca capturam um vídeo novo.
  conversationStartedAt: new Date().toISOString(),
  contextResetAt: null,     // última troca explícita de objetivo ("vamos mudar")
  // Contexto da conversa (efêmero, resetado em nova conversa)
  lastDiagnosis: null,      // { symptom, game, weapon, adjustment, at }
  lastTraining: null,       // { drillId, drillGoal, game, at }
  lastAnalysis: null,       // { capabilityId, game, analysisGoal, at }
  lastCalculation: null,    // { game, baseValue, at }
  lastVideo: null,          // { name, size, at } — vídeo mais recente da conversa
  currentObjective: null,   // texto curto do objetivo ativo
  activeSkill: null,        // capabilityId ativo
  // Slot filling conversacional (efêmero): a Mi lembra o que perguntou e
  // interpreta a próxima resposta livre ("sim, full" → gyroMode).
  pendingSkill: null,       // 'sensitivity' | 'calculator' | 'training' | 'video_goal' | ...
  pendingSlots: [],
  slots: {},
  // Memória permanente do jogador (PDS §13)
  playerMemory: {
    mainGame: null,         // 'pubg_mobile' | 'free_fire' | 'codm'
    controlMethod: null,    // 'gyro_full' | 'no_gyro' | ...
    fingers: null,
    device: null,
    playstyle: null         // 'rush' | 'support' | 'sniper'
  }
});

// Reidrata contexto salvo, tolerante a dados parciais/antigos.
export const hydrateContext = (saved) => {
  const base = createContext();
  if (!saved || typeof saved !== 'object') return base;
  return {
    ...base,
    ...saved,
    // A reidratação abre uma conversa NOVA: o carimbo é sempre o de agora,
    // mesmo que dados antigos tragam lastDiagnosis/lastTraining de outra sessão.
    conversationStartedAt: base.conversationStartedAt,
    contextResetAt: null,
    lastVideo: null,
    playerMemory: { ...base.playerMemory, ...(saved.playerMemory || {}) }
  };
};

export const updateContext = (ctx, key, data) => ({ ...ctx, [key]: data });

export const updatePlayerMemory = (ctx, patch = {}) => ({
  ...ctx,
  playerMemory: { ...ctx.playerMemory, ...patch }
});

// Nova conversa limpa contexto de conversa mas PRESERVA playerMemory.
export const resetConversationContext = (ctx) => ({
  ...createContext(),
  playerMemory: { ...(ctx?.playerMemory || createContext().playerMemory) }
});

// Registra o resultado da capability montada — chamado pós-mount.
export const updateFromResult = (ctx, capabilityId, mountContext = {}) => {
  const at = new Date().toISOString();
  if (capabilityId === 'sensitivity_diagnostic') {
    return updateContext(ctx, 'lastDiagnosis', {
      symptom: mountContext.symptom || null,
      game: mountContext.game || ctx.playerMemory.mainGame,
      weapon: mountContext.weapon || null,
      at
    });
  }
  if (capabilityId === 'training_single_drill') {
    return updateContext(ctx, 'lastTraining', {
      drillId: mountContext.drillId || null,
      drillGoal: mountContext.drillGoal || mountContext.rawText || null,
      game: mountContext.game || ctx.playerMemory.mainGame,
      at
    });
  }
  if (typeof capabilityId === 'string' && capabilityId.startsWith('mi_analysis')) {
    return updateContext(ctx, 'lastAnalysis', {
      capabilityId,
      game: mountContext.game || ctx.playerMemory.mainGame,
      analysisGoal: mountContext.validationGoal || capabilityId,
      at
    });
  }
  if (typeof capabilityId === 'string' && capabilityId.startsWith('calculator_')) {
    return updateContext(ctx, 'lastCalculation', {
      game: mountContext.game || null,
      baseValue: mountContext.baseValue || null,
      at
    });
  }
  return ctx;
};

// Resolve o jogo ativo. Prioridade: entidade explícita da mensagem >
// memória permanente do jogador > jogo do host. Nunca mistura jogos:
// se a mensagem cita CODM, contexto anterior de PUBG não vaza.
export const resolveGame = (ctx, explicitGame, hostGame) =>
  explicitGame || ctx?.playerMemory?.mainGame || hostGame || 'pubg_mobile';

// Extrai só o que pode ser persistido (nunca tokens, vídeo, payloads ricos).
export const serializeContext = (ctx) => ({
  lastDiagnosis: ctx.lastDiagnosis,
  lastTraining: ctx.lastTraining,
  lastAnalysis: ctx.lastAnalysis
    ? { capabilityId: ctx.lastAnalysis.capabilityId, game: ctx.lastAnalysis.game, analysisGoal: ctx.lastAnalysis.analysisGoal, at: ctx.lastAnalysis.at }
    : null,
  lastCalculation: ctx.lastCalculation,
  playerMemory: ctx.playerMemory
});

const contextEngine = {
  createContext,
  hydrateContext,
  updateContext,
  updatePlayerMemory,
  resetConversationContext,
  updateFromResult,
  resolveGame,
  serializeContext
};

export default contextEngine;
